<template>
  <v-form ref="atestadoForm" class="atestado-form">
    <v-row>
      <v-col class="px-0">
        <div class="edit-file-container" :style="{ background: $vuetify.theme.themes.light.soft }">
          <div class="icon-atestado" :style="{ background: $vuetify.theme.themes.light.tertiary }">
            <v-icon color="primary">mdi-file-document-multiple-outline</v-icon>
          </div>
          <div class="d-flex flex-column">
            <span class="edit-file-title">Você enviou um atestado</span>
            <span
              class="edit-file-link"
              :style="{ color: $vuetify.theme.themes.light.secondary }"
              @click="$emit('go-to-upload-file')"
              >Clique aqui para editar esse arquivo</span
            >
          </div>
        </div>
      </v-col>

      <v-col cols="12" class="mt-2">
        Tipo do atestado
        <v-radio-group
          v-model="atestado.tipo"
          :column="false"
          :rules="[() => $v.atestado.tipo.required || 'Campo obrigatório']"
        >
          <v-radio color="primary" label="Médico" value="medico"></v-radio>
          <v-spacer></v-spacer>
          <v-radio color="primary" label="Odontológico" value="odontologico"></v-radio>
        </v-radio-group>
      </v-col>
      <v-col cols="12" class="mt-2">
        Informações do atestado
      </v-col>

      <v-col cols="12" sm="7" class="mt-2">
        <v-text-field
          background-color="#FAFAFA"
          v-model="atestado.documentNumber"
          class="atestado-form-text-input"
          :label="documentNumberLabel"
          @keypress="onlyNumbers"
          outlined
          hint="Ex: 12345, 123456"
          ref="documentNumberRef"
          :rules="[
            () => $v.atestado.documentNumber.required || 'Campo obrigatório',
            () => $v.atestado.documentNumber.minLength || 'O número deve ter mais que 6 dígitos'
          ]"
        >
          <template slot="prepend-inner">
            <v-icon :color="$refs.documentNumberRef && $refs.documentNumberRef.valid ? 'success' : '#E2E2E2'">
              mdi-check-circle-outline
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="5" class="mt-2">
        <v-text-field
          background-color="#FAFAFA"
          v-model="atestado.documentState"
          class="atestado-form-text-input"
          :label="documentStateLabel"
          v-mask="['AA']"
          outlined
          hint="Ex: MG, SP"
          ref="documento"
          :rules="[
            () => $v.atestado.documentState.required || 'Campo obrigatório',
            () => $v.atestado.documentState.minLength || 'O estado precisa ter 2 letras',
            () => $v.atestado.documentState.validateEstado || 'Estado inválido'
          ]"
        >
          <template slot="prepend-inner">
            <v-icon :color="$refs.documento && $refs.documento.valid ? 'success' : '#E2E2E2'">
              mdi-check-circle-outline
            </v-icon>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" class="mt-2">
        <v-text-field
          background-color="#FAFAFA"
          v-model="atestado.nome"
          class="atestado-form-text-input"
          :label="nomeLabel"
          outlined
          ref="nome"
          :rules="[
            () => $v.atestado.nome.required || 'Campo obrigatório',
            () => $v.atestado.nome.minLength || 'Campo invalido'
          ]"
        >
          <template slot="prepend-inner">
            <v-icon :color="$refs.nome && $refs.nome.valid ? 'success' : '#E2E2E2'">
              mdi-check-circle-outline
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" class="mt-2">
        <v-autocomplete
          v-model="atestado.cids"
          :items="cids"
          @update:search-input="onSearchCids"
          :loading="loadingCids"
          :item-text="item => `${item.codigo} - ${item.nome}`"
          :label="'CID ou Nome da doença'"
          :placeholder="'CID ou Nome da doença'"
          :returnObject="true"
          :multiple="true"
          chips
          small-chips
          deletable-chips
          :rules="[() => $v.atestado.cids.required || 'Campo obrigatório']"
          noDataText="CID ou Nome para inicar a busca"
          outlined
          cache-items
          ref="cid"
          background-color="#FAFAFA"
          :menu-props="{ bottom: true, offsetY: true, zIndex: 2 }"
        >
          <template #prepend-inner>
            <v-icon :color="$refs.cid && atestado.cids.length > 0 ? 'success' : '#E2E2E2'">
              mdi-check-circle-outline
            </v-icon>
          </template>
          <template #selection="{ item, parent }">
            <v-chip small close @click:close="parent.selectItem(item)" style="max-width: 95%;">
              <span class="text-truncate"> {{ item.codigo }} - {{ item.nome }} </span>
            </v-chip>
          </template>

          <template #item="{ item }">
            <!-- prettier-ignore -->
            <template v-if="(typeof item !== 'object')">
              <v-list-item-content v-text="item"></v-list-item-content>
            </template>
            <template v-else>
              <v-list-item-content>
                <v-list-item-title>{{ item.codigo }} - {{ item.nome }}</v-list-item-title>
                <v-list-item-subtitle>Versão {{ item.version.split('_').join(': ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" class="mt-2">
        <v-text-field
          background-color="#FAFAFA"
          v-model="atestado.dtEmissao"
          class="atestado-form-text-input"
          label="Data da emissão"
          outlined
          ref="dtEmissao"
          v-mask="'##/##/####'"
          :rules="[
            () => $v.atestado.dtEmissao.required || 'Campo obrigatório',
            () => $v.atestado.dtEmissao.validDate || 'Data Incorreta'
          ]"
        >
          <template slot="prepend-inner">
            <v-icon :color="$refs.dtEmissao && $refs.dtEmissao.valid ? 'success' : '#E2E2E2'">
              mdi-check-circle-outline
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="mt-2">
        <v-text-field
          background-color="#FAFAFA"
          v-model="atestado.diasAfastamento"
          hint="Caso não necessite afastamento, preencha com 0"
          label="Dias de afastamento"
          @keypress="onlyNumbers"
          class="atestado-form-text-input"
          outlined
          ref="diasAfastamento"
          :rules="[() => $v.atestado.diasAfastamento.required || 'Campo obrigatório']"
        >
          <template slot="prepend">
            <div
              class="number-input-button"
              :style="{ background: $vuetify.theme.themes.light.primary }"
              @click="atestado.diasAfastamento > 0 ? atestado.diasAfastamento-- : null"
            >
              <v-icon size="15" color="#FFF">
                mdi-minus
              </v-icon>
            </div>
          </template>
          <template slot="append">
            <div
              class="number-input-button"
              :style="{ background: $vuetify.theme.themes.light.primary }"
              @click="atestado.diasAfastamento++"
            >
              <v-icon size="15" color="#FFF">
                mdi-plus
              </v-icon>
            </div>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" class="mt-2">
        Informações do atestado
      </v-col>
      <v-col cols="12">
        <v-textarea
          background-color="#FAFAFA"
          v-model="atestado.observacoes"
          class="atestado-form-text-input"
          label="Diga como você está se sentindo"
          outlined
        >
        </v-textarea>
      </v-col>
      <v-col cols="12">
        <v-checkbox :rules="[() => $v.shareData.sameAs || 'Campo obrigatório']" v-model="shareData">
          <template slot="label">
            <span class="checkbox-label"> Compartilhar meus dados de saúde com o departamento médico </span>
          </template>
        </v-checkbox>
      </v-col>
      <v-col cols="12" class="text-center">
        <v-btn color="primary" @click="saveAtestado()" :loading="loading">Seguir</v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'
import atestadoService from '@/services/atestado'
import cidService from '@/services/cid'
import getFileFromUrl from '@/utils/getFileFromUrl'
import { ESTADOS, dateRegex } from '@/utils/constants'

export default {
  name: 'AtestadoInfoForm',
  props: {
    atestado: {
      required: true
    }
  },
  data() {
    return {
      loadingCids: false,
      shareData: false,
      loading: false,
      cids: [],
      estados: ESTADOS,
      search: '',
      timerId: null
    }
  },
  mixins: [validationMixin],
  validations: {
    atestado: {
      tipo: {
        required
      },
      nome: {
        required,
        minLength: minLength(3)
      },
      dtEmissao: {
        required,
        validDate(dtEmissao) {
          return dateRegex.test(dtEmissao)
        }
      },
      diasAfastamento: {
        required
      },
      documentState: {
        required,
        minLength: minLength(2),
        validateEstado(state) {
          if (state) {
            if (state) {
              return this.estados.includes(state)
            }
            return false
          }
        }
      },
      documentNumber: {
        required,
        minLength: minLength(5)
      },
      cids: {
        required
      }
    },
    shareData: {
      sameAs: val => val === true
    }
  },
  computed: {
    nomeLabel() {
      return this.atestado.tipo === 'odontologico' ? 'Nome do(a) dentista' : 'Nome do(a) médico(a)'
    },
    documentNumberLabel() {
      return this.atestado.tipo === 'odontologico' ? 'Número do CRO do(a) dentista' : 'Número do CRM do(a) médico(a)'
    },
    documentStateLabel() {
      return this.atestado.tipo === 'odontologico' ? 'Estado do CRO do(a) dentista' : 'Estado do CRM do(a) médico(a)'
    }
  },
  methods: {
    onlyNumbers: function(evt) {
      evt = evt ? evt : window.event
      let expect = evt.target.value.toString() + evt.key.toString()

      if (!/^[-+]?[0-9]*\.?[0-9]*$/.test(expect)) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    async saveAtestado() {
      this.atestado.beneficiario = 'pessoal'
      this.atestado.documento = `${this.atestado.documentNumber}-${this.atestado.documentState}`
      if (this.$refs.atestadoForm.validate()) {
        try {
          this.loading = true
          this.atestado.id
            ? await atestadoService.updateAtestado(this.atestado)
            : await atestadoService.storeAtestado(this.atestado)

          this.$emit('atestado-saved')
        } catch ({ response }) {
          this.$showErrorMessage(response.data.message)
        } finally {
          this.loading = false
        }
      }
    },
    async getCids(search) {
      try {
        this.loadingCids = true
        const { data } = await cidService.buscaCids(search)
        this.cids = data
      } catch ({ response }) {
        this.$showErrorMessage(response.data.message)
      } finally {
        this.loadingCids = false
      }
    },
    async fetchFile() {
      await getFileFromUrl(this.atestado.arquivo, 'temp')
    },
    onSearchCids(search) {
      clearTimeout(this.timerId)

      if (!search || typeof search !== 'string') {
        return
      }

      if (search.length < 2) {
        return
      }

      this.timerId = setTimeout(() => {
        this.getCids(search)
      }, 700)
    }
  },
  mounted() {
    if (this.atestado.cids.length > 0) {
      this.cids = this.atestado.cids
    }
  }
}
</script>

<style scoped>
.icon-atestado {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-content: center;
  margin-right: 4vw;
}
.edit-file-container {
  background-color: #f5f8fc;
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: center;
  justify-content: center;
  font-size: 0.9rem;
  margin-top: 15px;
}
.edit-file-link {
  cursor: pointer;
  text-decoration: underline;
}
.edit-file-title {
  color: #acadbe;
}
.atestado-form >>> .theme--light.v-input input {
  color: #9b9cb1 !important;
}
.atestado-form >>> .v-text-field--outlined fieldset {
  border-color: transparent !important;
}
.number-input-button {
  border-radius: 5px;
  width: 20px;
  height: 20px;
  text-align: center;
}
.number-input-button:hover {
  transform: scale(1.1);
  cursor: pointer;
}
.custom-checkbox:before {
  border: 1px solid red;
}
.checkbox-label {
  font-size: 0.8rem !important;
}
</style>
